import { baseApi } from "@/config/http-common";
import {
  HrEmployeePayload,
  HrNotificationRequest,
  HrNotificationSettings,
} from "@/types/hr/Hr";

class HrDataService {
  getAnalytics(params: Record<string, unknown>) { return baseApi.get("/hr/analytics", { params }); }
  getAnalyticsLayout() { return baseApi.get("/hr/analytics/layout"); }
  saveAnalyticsLayout(payload: { columns: number; widgets: string[] }) { return baseApi.put("/hr/analytics/layout", payload); }
  getAudit(params: {
    employeeId?: string;
    action?: string;
    from?: string;
    to?: string;
    page: number;
  }) {
    return baseApi.get("/hr/audit", { params });
  }

  getNotificationSettings() {
    return baseApi.get("/hr/notifications/settings");
  }

  saveNotificationSettings(payload: HrNotificationSettings) {
    return baseApi.put("/hr/notifications/settings", payload);
  }

  previewNotifications(payload: HrNotificationRequest) {
    return baseApi.post("/hr/notifications/preview", payload);
  }

  sendNotifications(payload: HrNotificationRequest) {
    return baseApi.post("/hr/notifications/send", payload);
  }

  setExpiryNotifications(employeeIds: string[], enabled: boolean) {
    return baseApi.put("/hr/notifications/expiry", { employeeIds, enabled });
  }

  getNotificationDeliveries(employeeId: string) {
    return baseApi.get(`/hr/notifications/employees/${employeeId}`);
  }

  retryNotification(id: string) {
    return baseApi.post(`/hr/notifications/${id}/retry`);
  }

  getEmployees(params: Record<string, unknown>) {
    return baseApi.get("/hr/employees", { params });
  }

  getEmployeeSelection(params: Record<string, unknown>) {
    return baseApi.get("/hr/employees/selection", { params });
  }

  getEmployee(id: string) {
    return baseApi.get(`/hr/employees/${id}`);
  }

  createEmployee(payload: HrEmployeePayload) {
    return baseApi.post("/hr/employees", payload);
  }

  updateEmployee(id: string, payload: HrEmployeePayload) {
    return baseApi.put(`/hr/employees/${id}`, payload);
  }
  archiveEmployee(id: string) {
    return baseApi.delete(`/hr/employees/${id}`);
  }

  getReferenceData() {
    return baseApi.get("/hr/employees/reference-data");
  }

  getEmployeeTypes() {
    return baseApi.get("/hr/employee-types");
  }

  getDocumentTypes(includeArchived = false) {
    return baseApi.get("/hr/document-types", { params: { includeArchived } });
  }

  createDocumentType(payload: Record<string, unknown>) {
    return baseApi.post("/hr/document-types", payload);
  }

  updateDocumentType(id: string, payload: Record<string, unknown>) {
    return baseApi.put(`/hr/document-types/${id}`, payload);
  }

  archiveDocumentType(id: string) {
    return baseApi.delete(`/hr/document-types/${id}`);
  }

  uploadEmployeeDocument(employeeId: string, form: FormData) {
    return baseApi.post(`/hr/employees/${employeeId}/documents/upload`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  linkEmployeeDocument(employeeId: string, payload: Record<string, unknown>) {
    return baseApi.post(`/hr/employees/${employeeId}/documents/link`, payload);
  }

  archiveEmployeeDocument(employeeId: string, documentId: string) {
    return baseApi.delete(
      `/hr/employees/${employeeId}/documents/${documentId}`
    );
  }

  updateResidentialAddress(id: string, residentialAddress: string) {
    return baseApi.put(`/hr/employees/${id}/address`, { residentialAddress });
  }

  classifyEmployeeDocument(employeeId: string, documentId: string, payload: Record<string, unknown>) {
    return baseApi.put(`/hr/employees/${employeeId}/documents/${documentId}/classification`, payload);
  }

  updateEmployeeDocumentDates(employeeId: string, documentId: string, payload: Record<string, unknown>) {
    return baseApi.put(`/hr/employees/${employeeId}/documents/${documentId}/dates`, payload);
  }

  downloadEmployeeDocument(
    employeeId: string,
    documentId: string,
    inline = false
  ) {
    return baseApi.get(
      `/hr/employees/${employeeId}/documents/${documentId}/file`,
      {
        params: { inline },
        responseType: "blob",
      }
    );
  }

  downloadEsignArchive(employeeId: string, documentId: string) {
    return baseApi.get(
      `/hr/employees/${employeeId}/documents/${documentId}/esign-archive`,
      { responseType: "blob" }
    );
  }

  previewEmployeeDocument(employeeId: string, documentId: string) {
    return baseApi.get(`/hr/employees/${employeeId}/documents/${documentId}/preview`, {
      responseType: "blob",
    });
  }

  searchEsignDocuments(query: string, page = 1) {
    return baseApi.get("/hr/esign-documents", { params: { query, page } });
  }

  getAssistantAccessSettings() {
    return baseApi.get("/hr/assistant-access");
  }

  searchAssistantAccessEmployees(query: string) {
    return baseApi.get("/hr/assistant-access/employees", { params: { query } });
  }

  updateAssistantRoleAccess(roleId: string, payload: Record<string, unknown>) {
    return baseApi.put(`/hr/assistant-access/roles/${roleId}`, payload);
  }

  downloadAssistantDocument(employeeId: string, documentId: string) {
    return baseApi.get(
      `/hr/assistant/employees/${employeeId}/documents/${documentId}/file`,
      { responseType: "blob" }
    );
  }
}

export default new HrDataService();
